/**
 * TermsAgreementModal — mandatory, version-aware, one-time Terms acceptance.
 *
 * Shown globally (from AuthProvider) whenever the authenticated user's
 * server-computed `termsAcceptanceRequired` is true — i.e. they have never
 * accepted the current Terms & User Agreement version, or a Terms update
 * since their last acceptance made their stored version stale. Structurally
 * identical to RoleSelectionModal: non-dismissible (no backdrop click, no
 * Escape key), rendered via a portal, gated purely on server state.
 *
 * Content mirrors the summary previously shown inline in Login.tsx's modal
 * (full legal text lives at /terms and the other routes in PolicyPage.tsx's
 * POLICY_CATALOG) — not duplicated/rewritten, just relocated so it can be
 * shown post-login (logout/login, version bump) and not only pre-login.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ExternalLink, ShieldCheck } from 'lucide-react';
import { acceptTerms, logout } from '@/src/modules/auth/session';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';

export function TermsAgreementModal({
  open,
  onDecline,
}: {
  open: boolean;
  onDecline?: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);

  async function handleDecline() {
    if (busy) return;
    if (onDecline) {
      try {
        await onDecline();
        return;
      } catch {
        // Fall back to built-in logout path if onDecline rejects
      }
    }
    try {
      await logout();
      window.location.href = '/login';
    } catch {
      window.location.href = '/login';
    }
  }

  async function handleAccept() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { termsAcceptedVersion } = await acceptTerms();
      import('@/src/infrastructure/lib/analytics').then(({ trackEvent }) => {
        trackEvent('POLICY_ACCEPTED', 'compliance', termsAcceptedVersion);
      });
      // Reload the snapshot — this clears termsAcceptanceRequired from durable
      // server state, the same pattern RoleSelectionModal uses for
      // roleSelectionRequired. The modal is never closed optimistically
      // before this round-trip confirms the backend recorded it.
      await loadCurrentUser();
      // loadCurrentUser() swallows its own errors (never rejects) — if the
      // freshly-reloaded snapshot still reports termsAcceptanceRequired
      // despite a successful accept call (a race, a stale read, a backend
      // bug), re-enable the button instead of leaving a permanently
      // disabled, non-dismissible modal with no recourse.
      if (useAuthStore.getState().termsAcceptanceRequired) {
        setError('Still shows as required — please try again.');
        setBusy(false);
      }
      // Otherwise `open` flips to false via AuthProvider's derived state and
      // this component unmounts — no need to reset `busy` on that path.
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
      setBusy(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Non-clickable backdrop — intentionally does NOT close modal */}
          <div
            data-testid="terms-modal-backdrop"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="InviteCloud Terms & User Agreement"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative z-10 w-full max-w-2xl rounded-3xl border border-outline-variant/30 bg-surface p-6 shadow-2xl sm:p-8 text-on-surface"
          >
            <div className="flex items-start gap-3 border-b border-outline-variant/20 pb-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-on-surface">
                  InviteCloud Terms & User Agreement
                </h3>
                <p className="text-xs text-on-surface-variant mt-0.5">
                  Please review and accept our platform policies to continue.{' '}
                  <span className="block sm:inline mt-1 sm:mt-0">
                    Read the full{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary font-semibold underline underline-offset-2 hover:text-primary-dark"
                    >
                      Terms
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary font-semibold underline underline-offset-2 hover:text-primary-dark"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </p>
              </div>
            </div>

            <div className="my-5 max-h-[min(60vh,520px)] overflow-y-auto space-y-4 pr-2 text-xs text-on-surface-variant leading-relaxed">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3.5 text-xs font-semibold text-primary flex items-center justify-between">
                <span>Accepting is required to continue using InviteCloud.</span>
                <span className="text-[11px] font-normal opacity-80">Scroll to read all 4 sections ↓</span>
              </div>

              <div className="rounded-xl border border-outline-variant/30 bg-surface-low p-4">
                <h4 className="font-bold text-on-surface text-sm mb-1">
                  1. Terms & Conditions & Jurisdiction
                </h4>
                <p>
                  The InviteCloud platform is operated by InviteCloud. By creating an account or logging in, you agree to our Terms of Service governed under Indian jurisdiction.
                </p>
              </div>

              <div className="rounded-xl border border-outline-variant/30 bg-surface-low p-4">
                <h4 className="font-bold text-on-surface text-sm mb-1">
                  2. Privacy, DPDP Act & Telemetry
                </h4>
                <p>
                  We collect minimal personal data under the DPDP Act. Analytics (GA4) tracks aggregated pageviews and template selections with <strong>zero PII captured</strong>. Contact us at <a href="mailto:support@invitecloud.in" className="text-primary underline">support@invitecloud.in</a> or <a href="mailto:founder@invitecloud.in" className="text-primary underline">founder@invitecloud.in</a>.
                </p>
              </div>

              <div className="rounded-xl border border-outline-variant/30 bg-surface-low p-4">
                <h4 className="font-bold text-on-surface text-sm mb-1">
                  3. Acceptable Use & AI Features
                </h4>
                <p>
                  All uploaded content must follow our Acceptable Use Policy. We may review uploaded media and text and remove content that breaks it. AI features are optional; when you use one, trusted third-party AI providers receive only the content that feature needs.
                </p>
              </div>

              <div className="rounded-xl border border-outline-variant/30 bg-surface-low p-4">
                <h4 className="font-bold text-on-surface text-sm mb-1">
                  4. Payments, Credits & Dispute Resolution
                </h4>
                <p>
                  Partner credit packs and one-time purchases are processed via Razorpay only. Prices are shown in INR and are inclusive of all applicable taxes; GST is not currently charged. Every purchase carries an order number (<code>ORD-...</code>) and a payment reference (<code>pay_...</code>) so support can trace it.
                </p>
              </div>

              {/* TODO(owner): Product/legal review requested to align §3 and §4 copy with #247 (1-time purchases vs subscriptions) and C4 support email alias. */}

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                >
                  View Full Policy Hub (/terms) <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                >
                  View Privacy Policy (/privacy-policy) <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {error && (
              <p role="alert" className="mb-4 text-xs text-status-failed text-center">
                {error}
              </p>
            )}

            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 border-t border-outline-variant/20 pt-4">
              <button
                type="button"
                onClick={handleDecline}
                disabled={busy}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-outline-variant/40 bg-surface hover:bg-surface-low text-on-surface-variant hover:text-on-surface text-xs font-bold transition-all disabled:opacity-60 disabled:pointer-events-none"
              >
                Decline & sign out
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={busy}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-on-primary text-xs font-bold shadow-md hover:bg-primary-dark transition-all disabled:opacity-60 disabled:pointer-events-none"
              >
                {busy ? (
                  <span className="h-4 w-4 rounded-full border-2 border-on-primary/60 border-t-transparent animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {busy ? 'Saving…' : 'I Agree & Continue'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
